// OutboundStore — MongoDB-backed cold-call queue via /api/leads?outbound=true.
// Deliberately separate from leadsStore so the primary CRM cache never
// warms with tens of thousands of cold rows.
//
// The store polls its own scoped payload (my queue for LOs, everything
// for admins) every 45s. Server-side filtering by outboundStatus is used
// so switching filter chips only pulls the rows the LO is about to see.

import { useSyncExternalStore } from 'react';
import axios from 'axios';
import { getStoredUser } from './authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 45000;

let cache = [];
let stats = {
  total: 0, unworked: 0, contacted: 0, follow_ups: 0,
  appointments: 0, calls_today: 0, hot_leads: 0,
};
let cacheVersion = 0;
let _lastSig = '';
let _loaded = false;
const subs = new Set();
let _hydrated = false;
let _pollTimer = null;

function emit() {
  cacheVersion += 1;
  subs.forEach((fn) => { try { fn(); } catch {} });
}
function snapshot() { return cacheVersion; }

async function fetchAll() {
  const me = getStoredUser();
  const scoped = me?.role !== 'admin' && me?.id ? { assigned_to: me.id } : {};
  // Why 50_000: 10k-lead CSV imports are now a daily reality (Veterans
  // United list = 14k). The old 5000 ceiling silently truncated the
  // cache so filter tabs ("New", "Attempted") showed partial counts
  // even though the KPI knew about the full set. 50k gives us ~5x
  // headroom at a ~25MB JSON cost — the next step (proper server-side
  // pagination keyed off the active filter) will trim this later.
  const outboundParams = { light: true, outbound: true, limit: 50000, ...scoped };
  // Recently-promoted leads (outbound=false + outboundStatus='hot') so the
  // Outbound page's Hot chip still reflects promotions this LO made — the
  // cache below merges them in but they've already left `outbound=true`.
  const promotedParams = { light: true, outbound_status: 'hot', limit: 500, ...scoped };
  try {
    const [leadsRes, promotedRes, statsRes] = await Promise.all([
      axios.get(`${API}/leads`, { params: outboundParams }),
      axios.get(`${API}/leads`, { params: promotedParams }),
      axios.get(`${API}/outbound/stats`),
    ]);
    const rawRows = Array.isArray(leadsRes.data) ? leadsRes.data : [];
    const promoted = Array.isArray(promotedRes.data) ? promotedRes.data : [];
    // Merge — de-dupe by id. `not_interested` / `opt_out` / `wrong_number`
    // leads are intentionally NOT fetched: once a disposition exits the
    // dialer the LO shouldn't see the row again from any dialer view.
    // The data still lives in the DB for Reports queries.
    const byId = new Map();
    rawRows.forEach((l) => byId.set(l.id, l));
    promoted.forEach((l) => { if (!byId.has(l.id)) byId.set(l.id, l); });
    const rows = Array.from(byId.values());
    const sig = rows.length + ':' + rows.reduce(
      (s, l) => s + (l.id?.charCodeAt(0) || 0) + (l.outboundStatus?.charCodeAt(0) || 0) + (l.outboundAttempts || 0),
      0,
    );
    stats = statsRes.data || stats;
    if (sig === _lastSig && _loaded) { emit(); return cache; }
    _lastSig = sig;
    cache = rows;
    _loaded = true;
    emit();
    return cache;
  } catch (e) {
    console.warn('OutboundStore.fetchAll failed', e?.message || e);
    _loaded = true;
    emit();
    return cache;
  }
}

function ensureHydrated() {
  if (_hydrated) return;
  _hydrated = true;
  fetchAll();
  let inflight = false;
  _pollTimer = setInterval(() => {
    if (_pollPaused || inflight || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
    inflight = true;
    fetchAll().finally(() => { inflight = false; });
  }, POLL_MS);
  if (typeof window !== 'undefined') {
    window.addEventListener('kf:auth-change', () => {
      _lastSig = '';
      _loaded = false;
      cache = [];
      fetchAll();
    });
  }
}

// Pause/resume the background poll. FocusMode calls `pausePolling()`
// when it mounts so the LO's status clicks don't compete with an
// in-flight 50k-row list fetch for the browser's 6-connection HTTP/1.1
// pool. Resumed on unmount so the dashboard KPIs keep refreshing.
// Ref-counted so overlapping callers don't accidentally unpause each
// other.
let _pollPaused = 0;
function pausePolling() {
  _pollPaused += 1;
}
function resumePolling() {
  _pollPaused = Math.max(0, _pollPaused - 1);
  if (_pollPaused === 0) {
    // Fire an immediate catch-up fetch so the resumed caller sees the
    // latest state right away instead of waiting up to POLL_MS.
    fetchAll();
  }
}

function subscribe(fn) {
  subs.add(fn);
  ensureHydrated();
  return () => subs.delete(fn);
}

// ── Selectors ──────────────────────────────────────────────────────────
function getAll() { return cache; }
function getStats() { return stats; }
function isLoaded() { return _loaded; }

// ── Mutations ──────────────────────────────────────────────────────────
async function logCall(leadId, payload) {
  try {
    const r = await axios.post(`${API}/outbound/leads/${leadId}/log-call`, payload);
    const fresh = r.data;
    // Cache rules (updated Feb 10, 2026):
    //   • Hot-promoted leads (outbound=false + outboundStatus='hot')
    //     STAY in the cache so the Hot chip still counts them.
    //   • `not_interested` / `opt_out` / `wrong_number` / any other
    //     dialer-exit disposition is a HARD exit — evict immediately
    //     so the row disappears from every dialer view. The data lives
    //     on in the DB for Reports but the LO never sees it again
    //     while they're dialing.
    const KEEP_IN_CACHE = new Set(['hot']);
    if (fresh?.outbound === false && !KEEP_IN_CACHE.has(fresh?.outboundStatus)) {
      cache = cache.filter((l) => l.id !== leadId);
    } else {
      cache = cache.map((l) => (l.id === leadId ? { ...l, ...fresh } : l));
    }
    emit();
    // Refresh stats in the background so KPI cards update.
    axios.get(`${API}/outbound/stats`).then((s) => { stats = s.data || stats; emit(); }).catch(() => {});
    return fresh;
  } catch (e) {
    console.error('OutboundStore.logCall failed', e?.response?.data || e);
    return null;
  }
}

async function updateStatus(leadId, outboundStatus, extras = {}) {
  try {
    const r = await axios.patch(`${API}/leads/${leadId}`, { outboundStatus, ...extras });
    const fresh = r.data;
    // Same KEEP_IN_CACHE policy as logCall — only `hot` promotions
    // linger in the dialer cache after `outbound=false`. Everything
    // else evicts so the LO stops seeing the lead immediately.
    const KEEP_IN_CACHE = new Set(['hot']);
    if (fresh?.outbound === false && !KEEP_IN_CACHE.has(fresh?.outboundStatus)) {
      cache = cache.filter((l) => l.id !== leadId);
    } else {
      cache = cache.map((l) => (l.id === leadId ? { ...l, ...fresh } : l));
    }
    emit();
    axios.get(`${API}/outbound/stats`).then((s) => { stats = s.data || stats; emit(); }).catch(() => {});
    return fresh;
  } catch (e) {
    console.error('OutboundStore.updateStatus failed', e?.response?.data || e);
    return null;
  }
}

// ── React hooks ────────────────────────────────────────────────────────
export function useOutbound() {
  useSyncExternalStore(subscribe, snapshot);
  return getAll();
}
export function useOutboundStats() {
  useSyncExternalStore(subscribe, snapshot);
  return getStats();
}
export function useOutboundLoaded() {
  useSyncExternalStore(subscribe, snapshot);
  return isLoaded();
}

export const OutboundStore = {
  getAll,
  getStats,
  refresh: fetchAll,
  logCall,
  updateStatus,
  pausePolling,
  resumePolling,
};
